import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { ManagedFeatureFlag, ManagedFeatureFlagsService } from '../../../services/managed-feature-flags/managed-feature-flags.service';
import { FlagLifecycleInput, OUTCOME_LABELS, PURPOSE_LABELS, REASON_LABELS, STAGE_LABELS } from '../../../services/managed-feature-flags/managed-feature-flag-lifecycle';

@Component({
  selector: 'app-managed-feature-flag-lifecycle-dialog', standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatButtonModule],
  templateUrl: './managed-feature-flag-lifecycle-dialog.component.html',
  styles: [`
    .lifecycle-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 16px; }
    .wide { grid-column: 1 / -1; } .error { color: #b42318; } .notice { color: #475467; }
    .reason { display: inline-block; padding: 4px 8px; margin: 0 6px 6px 0; background: #fff4db; border-radius: 4px; }
    mat-dialog-actions { flex-wrap: wrap; gap: 8px; } fieldset { border: 0; padding: 0; margin: 0; min-width: 0; }
    @media(max-width: 600px) { .lifecycle-grid { grid-template-columns: 1fr; } }
  `],
})
export class ManagedFeatureFlagLifecycleDialogComponent {
  readonly purposes = Object.entries(PURPOSE_LABELS);
  readonly outcomes = Object.entries(OUTCOME_LABELS);
  readonly stages = Object.entries(STAGE_LABELS);
  readonly reasonLabels = REASON_LABELS;
  readonly data = inject<{ flag: ManagedFeatureFlag; backend: string }>(MAT_DIALOG_DATA);
  private readonly api = inject(ManagedFeatureFlagsService);
  private readonly ref = inject(MatDialogRef<ManagedFeatureFlagLifecycleDialogComponent>);
  private readonly changes = inject(ChangeDetectorRef);
  draft: FlagLifecycleInput = this.toDraft(this.data.flag);
  saving = false;
  error = '';
  message = '';
  conflict = false;
  private toDraft(flag: ManagedFeatureFlag): FlagLifecycleInput {
    const { purpose, owner, reviewDueDate, intendedOutcome, cleanupStage, workItemUrl, reviewNotes } = flag.lifecycle!;
    return { purpose, owner, reviewDueDate, intendedOutcome, cleanupStage, workItemUrl, reviewNotes };
  }
  get decisionChanged(): boolean {
    return this.draft.purpose !== this.data.flag.lifecycle?.purpose || this.draft.intendedOutcome !== this.data.flag.lifecycle?.intendedOutcome;
  }
  get dirty(): boolean { return JSON.stringify(this.draft) !== JSON.stringify(this.toDraft(this.data.flag)); }
  get reviewReady(): boolean {
    return !this.decisionChanged && this.draft.purpose !== 'unclassified' && !!this.draft.owner?.trim()
      && this.draft.intendedOutcome !== 'undecided' && !!this.draft.reviewNotes?.trim()
      && !!this.draft.reviewDueDate && this.draft.reviewDueDate > new Date().toISOString().slice(0, 10);
  }
  async save(recordReview: boolean): Promise<void> {
    if (this.saving || this.conflict || !this.data.flag.lifecycleVersion) return;
    this.saving = true; this.error = ''; this.message = '';
    try {
      this.data.flag = await this.api.updateLifecycle(this.data.flag.id, this.draft, this.data.flag.lifecycleVersion, recordReview);
      this.draft = this.toDraft(this.data.flag);
      this.message = recordReview ? 'Review recorded. Configuration suggestions are deferred until the next review date.' : 'Lifecycle saved.';
    } catch (error: any) {
      this.conflict = error?.status === 409;
      this.error = error?.data?.message || error?.error?.message || error?.message || 'Unable to save lifecycle.';
    } finally { this.saving = false; this.changes.markForCheck(); }
  }
  async refresh(): Promise<void> {
    this.saving = true;
    try {
      this.data.flag = await this.api.getById(this.data.flag.id);
      this.draft = this.toDraft(this.data.flag);
      this.conflict = false; this.error = ''; this.message = 'Current flag loaded. Review it before saving.';
    } catch { this.error = 'Unable to refresh this flag. Close and reload the page.'; }
    finally { this.saving = false; this.changes.markForCheck(); }
  }
  close(): void { if (!this.saving) this.ref.close(this.data.flag); }
}
